import React, { useState } from 'react';
import { Plus, Wifi, CheckCircle, Smartphone, X } from 'lucide-react';
import { 
  Property, 
  Area, 
  User, 
  Vehicle, 
  Bin, 
  Equipment, 
  Transaction, 
  UserRole, 
  TabType 
} from './types';
import { 
  sampleProperties, 
  sampleAreas, 
  sampleUsers, 
  sampleVehicles, 
  sampleBins, 
  sampleEquipment 
} from './data';
import { calculateTotal, generateReceiptNumber } from './utils';
import Header from './components/Header';
import Navigation from './components/Navigation';
import PropertiesTab from './components/PropertiesTab';
import PaymentModal from './components/PaymentModal';
import AssetsTab from './components/AssetsTab';
import UsersTab from './components/UsersTab';

const WasteManagementSystem = () => {
  // State Management
  const [activeTab, setActiveTab] = useState<TabType>('properties');
  const [userRole, setUserRole] = useState<UserRole>('admin');
  
  // Data States
  const [properties, setProperties] = useState<Property[]>(sampleProperties);
  const [areas, setAreas] = useState<Area[]>(sampleAreas);
  const [users, setUsers] = useState<User[]>(sampleUsers);
  const [vehicles, setVehicles] = useState<Vehicle[]>(sampleVehicles);
  const [bins, setBins] = useState<Bin[]>(sampleBins);
  const [equipment, setEquipment] = useState<Equipment[]>(sampleEquipment);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Modal States
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState<Transaction | null>(null);

  // Handlers
  const handlePaymentClick = (property: Property) => {
    setSelectedProperty(property);
    setShowPaymentModal(true);
  };

  const handlePayment = (paymentData: {
    amount: number;
    method: string;
    frequency: string;
  }) => {
    if (!selectedProperty) return;

    const { amount, method, frequency } = paymentData;
    const total = calculateTotal(selectedProperty);
    
    const newReceipt: Transaction = {
      id: Date.now(),
      propertyId: selectedProperty.id,
      propertyName: selectedProperty.name,
      plotNo: selectedProperty.plotNo,
      roadNo: selectedProperty.roadNo,
      amount: amount,
      paymentMethod: method as any,
      paymentFrequency: frequency as any,
      totalDue: total,
      remainingDue: Math.max(0, total - amount),
      date: new Date().toLocaleString(),
      receiptNo: generateReceiptNumber()
    };

    // Update property dues
    const updatedProperties = properties.map(prop => {
      if (prop.id === selectedProperty.id) {
        const remaining = Math.max(0, total - amount);
        return {
          ...prop,
          dues: Math.max(0, remaining - prop.monthlyRate),
          penalty: remaining > prop.monthlyRate ? Math.floor((remaining - prop.monthlyRate) * 0.1) : 0,
          paymentFrequency: frequency as any,
          lastPayment: new Date().toISOString().split('T')[0]
        };
      }
      return prop;
    });

    setProperties(updatedProperties);
    setCurrentReceipt(newReceipt);
    setTransactions([newReceipt, ...transactions]);
    setShowPaymentModal(false);
    setShowReceiptModal(true);
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'properties':
        return (
          <PropertiesTab
            properties={properties}
            areas={areas}
            userRole={userRole}
            onPaymentClick={handlePaymentClick}
          />
        );
      case 'payments':
        return <PaymentsTab transactions={transactions} />;
      case 'users':
        return userRole === 'admin' ? <UsersTab users={users} areas={areas} /> : null;
      case 'assets':
        return userRole === 'admin' ? <AssetsTab vehicles={vehicles} bins={bins} equipment={equipment} /> : null;
      case 'reports':
        return <ReportsTab properties={properties} transactions={transactions} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <Header
        userRole={userRole}
        setUserRole={setUserRole}
        transactions={transactions}
      />

      {/* Navigation */}
      <Navigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userRole={userRole}
      />

      {/* Main Content */}
      <div className="px-6 py-6 pb-24">
        {renderActiveTab()}
      </div>

      {/* Payment Modal */}
      {selectedProperty && (
        <PaymentModal
          property={selectedProperty}
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPayment={handlePayment}
        />
      )}

      {/* Receipt Modal */}
      {showReceiptModal && currentReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h2>
              <p className="text-gray-500">Digital receipt generated</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-6 space-y-3">
              <div className="text-center pb-3 border-b border-gray-200">
                <div className="text-xs text-gray-500">Receipt #</div>
                <div className="font-bold text-lg">{currentReceipt.receiptNo}</div>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Property:</span>
                <span className="font-semibold">{currentReceipt.propertyName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Plot No:</span>
                <span className="font-semibold">{currentReceipt.plotNo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Type:</span>
                <span className="font-semibold capitalize">{currentReceipt.paymentFrequency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Method:</span>
                <span className="font-semibold capitalize">{currentReceipt.paymentMethod}</span>
              </div>
              
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between text-lg">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-bold text-green-600">₹{currentReceipt.amount}</span>
                </div>
                {currentReceipt.remainingDue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remaining Due:</span>
                    <span className="font-semibold text-red-600">₹{currentReceipt.remainingDue}</span>
                  </div>
                )}
              </div>
              
              <div className="text-center pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">Payment Date</div>
                <div className="font-semibold">{currentReceipt.date}</div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Payment Receipt',
                      text: `Payment receipt for ${currentReceipt.propertyName} - ₹${currentReceipt.amount}`,
                      url: window.location.href
                    });
                  } else {
                    alert('Receipt details copied to clipboard!');
                  }
                }}
                className="w-full bg-blue-600 text-white py-3 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:bg-blue-700 transition-colors"
              >
                <Smartphone className="w-5 h-5" />
                <span>Share Receipt</span>
              </button>
              
              <button
                onClick={() => setShowReceiptModal(false)}
                className="w-full bg-gray-600 text-white py-3 rounded-2xl font-semibold hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <div className="fixed bottom-20 right-6 z-30">
        <button
          onClick={() => setActiveTab('payments')}
          className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
        >
          <Plus className="w-8 h-8" />
        </button>
      </div>

      {/* Status Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 z-40">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-gray-600 font-medium">Online</span>
            </div>
            <div className="text-gray-500">
              {new Date().toLocaleDateString('en-IN', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Wifi className="w-4 h-4 text-green-500" />
            <span className="text-gray-600 font-medium">Synced</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const PaymentsTab = ({ transactions }: { transactions: Transaction[] }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-900">Recent Payments</h2>
    {transactions.length === 0 ? (
      <div className="text-center py-12">
        <p className="text-gray-500">No payments recorded yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {transactions.map(transaction => (
          <div key={transaction.id} className="bg-white p-4 rounded-2xl border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">{transaction.propertyName}</p>
                <p className="text-sm text-gray-500">Receipt: {transaction.receiptNo}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-green-600">₹{transaction.amount}</p>
                <p className="text-xs text-gray-500">{transaction.date}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);





const ReportsTab = ({ properties, transactions }: { properties: Property[], transactions: Transaction[] }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-white p-6 rounded-2xl border border-gray-200">
        <h3 className="font-bold text-lg mb-2">Total Properties</h3>
        <p className="text-3xl font-bold text-blue-600">{properties.length}</p>
      </div>
      <div className="bg-white p-6 rounded-2xl border border-gray-200">
        <h3 className="font-bold text-lg mb-2">Total Collections</h3>
        <p className="text-3xl font-bold text-green-600">
          ₹{transactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
        </p>
      </div>
      <div className="bg-white p-6 rounded-2xl border border-gray-200">
        <h3 className="font-bold text-lg mb-2">Pending Dues</h3>
        <p className="text-3xl font-bold text-red-600">
          ₹{properties.reduce((sum, p) => sum + calculateTotal(p), 0).toLocaleString()}
        </p>
      </div>
    </div>
  </div>
);

export default WasteManagementSystem;